<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAllocationDisplay } from 'dashboard/components-next/planning/Scheduler/useAllocationDisplay';
import { useEventHoverCard } from 'dashboard/components-next/planning/Scheduler/useDayEventHoverCard';
import EventHoverCard from 'dashboard/components-next/planning/Scheduler/EventHoverCard.vue';

const props = defineProps({
  allocation: {
    type: Object,
    required: true,
  },
  date: {
    type: Date,
    required: true,
  },
  isChild: {
    type: Boolean,
    default: false,
  },
  parentAllocation: {
    type: Object,
    default: null,
  },
  slotItems: {
    type: Array,
    default: () => [],
  },
  resources: {
    type: Array,
    default: () => [],
  },
  disableResize: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'click', 'contextMenu']);
const { t } = useI18n();

const {
  getEventForAllocation,
  getEventAccentColor,
  getEventLabelColor,
  getDisplayTitle,
  formatTimeRange,
} = useAllocationDisplay();

const blockRef = ref(null);
const isDragging = ref(false);
const dragStartY = ref(0);
const dragOffset = ref(0);
const didDragOrResize = ref(false);
const isResizing = ref(false);
const resizeHandle = ref(null);
const resizeOffset = ref(0);

const SNAP_PX = 15;

const startOfDay = computed(() => {
  const d = new Date(props.date);
  d.setHours(0, 0, 0, 0);
  return d;
});

const topPosition = computed(() => {
  const start = new Date(props.allocation.start_at);
  return Math.max(0, (start - startOfDay.value) / 1000 / 60);
});

const blockHeight = computed(() => {
  const start = new Date(props.allocation.start_at);
  const end = new Date(props.allocation.end_at);
  return Math.max(SNAP_PX, (end - start) / 1000 / 60);
});

const isInvalid = computed(() => {
  return (
    props.allocation.status === 'invalid' ||
    props.allocation.status === 'cancelled'
  );
});

const linkedEvent = computed(() => getEventForAllocation(props.allocation));

const hoverEvent = computed(() => {
  if (linkedEvent.value) return linkedEvent.value;
  if (!props.allocation.event_name) return null;
  return {
    name: props.allocation.event_name,
    accent_color: props.allocation.accent_color,
    system_stage: props.allocation.metadata?.event_status,
  };
});

const accentColor = computed(() => getEventAccentColor(props.allocation));

const displayTitle = computed(() => getDisplayTitle(props.allocation));

const eventLabel = computed(() => linkedEvent.value?.event_label || null);

const eventLabelColor = computed(() => getEventLabelColor(props.allocation));

const timeRangeLabel = computed(() => formatTimeRange(props.allocation));

const hoverSlots = computed(() => {
  if (props.slotItems.length) return props.slotItems;
  const parentSubs = props.parentAllocation?.sub_allocations;
  if (parentSubs?.length) return parentSubs;
  return [props.allocation];
});

const hoveredSlotIds = computed(() => hoverSlots.value.map(slot => slot.id));

const slotCount = computed(() => hoverSlots.value.length || 1);

const hoverBlocked = computed(() => isDragging.value || isResizing.value);
const hoverEnabled = ref(true);

const {
  showHoverCard,
  hoverAnchorRect,
  startHoverCard,
  hideHoverCardSoon,
  keepHoverCard,
} = useEventHoverCard({
  enabled: hoverEnabled,
  blocked: hoverBlocked,
  anchorRef: blockRef,
});

const blockStyle = computed(() => {
  let top = topPosition.value;
  let height = blockHeight.value;

  if (isDragging.value) {
    top += dragOffset.value;
  }
  if (isResizing.value) {
    if (resizeHandle.value === 'top') {
      top += resizeOffset.value;
      height -= resizeOffset.value;
    } else {
      height += resizeOffset.value;
    }
  }

  const style = {
    top: `${Math.max(0, top)}px`,
    height: `${Math.max(SNAP_PX, height)}px`,
  };

  if (isInvalid.value) {
    style.backgroundColor = '#FDE2E4';
    style.borderColor = '#f87171';
  }

  return style;
});

const handleMouseDown = e => {
  if (e.button !== 0) return;
  if (e.target.closest('.resize-handle')) return;

  dragStartY.value = e.clientY;
  dragOffset.value = 0;

  document.addEventListener('mousemove', handleDragMove);
  document.addEventListener('mouseup', handleDragUp);

  e.preventDefault();
  e.stopPropagation();
};

const handleDragMove = e => {
  const rawOffset = e.clientY - dragStartY.value;

  if (!isDragging.value && Math.abs(rawOffset) < 5) return;
  isDragging.value = true;
  dragOffset.value = Math.round(rawOffset / SNAP_PX) * SNAP_PX;
};

const handleDragUp = () => {
  document.removeEventListener('mousemove', handleDragMove);
  document.removeEventListener('mouseup', handleDragUp);

  if (!isDragging.value) {
    return;
  }

  didDragOrResize.value = true;
  setTimeout(() => {
    didDragOrResize.value = false;
  }, 50);

  const shiftMs = dragOffset.value * 60 * 1000;
  const newStart = new Date(
    new Date(props.allocation.start_at).getTime() + shiftMs
  );
  const newEnd = new Date(
    new Date(props.allocation.end_at).getTime() + shiftMs
  );

  emit('update', {
    id: props.allocation.id,
    start_at: newStart.toISOString(),
    end_at: newEnd.toISOString(),
  });

  nextTick(() => {
    isDragging.value = false;
    dragOffset.value = 0;
  });
};

const handleResizeStart = (handle, e) => {
  if (e.button !== 0 || props.disableResize) return;

  isResizing.value = true;
  resizeHandle.value = handle;
  dragStartY.value = e.clientY;
  resizeOffset.value = 0;

  document.addEventListener('mousemove', handleResizeMove);
  document.addEventListener('mouseup', handleResizeEnd);

  e.preventDefault();
  e.stopPropagation();
};

const handleResizeMove = e => {
  const rawOffset = e.clientY - dragStartY.value;
  resizeOffset.value = Math.round(rawOffset / SNAP_PX) * SNAP_PX;
};

const handleResizeEnd = () => {
  document.removeEventListener('mousemove', handleResizeMove);
  document.removeEventListener('mouseup', handleResizeEnd);

  const shiftMs = resizeOffset.value * 60 * 1000;
  let newStart = props.allocation.start_at;
  let newEnd = props.allocation.end_at;

  if (resizeHandle.value === 'top') {
    newStart = new Date(
      new Date(props.allocation.start_at).getTime() + shiftMs
    ).toISOString();
  } else {
    newEnd = new Date(
      new Date(props.allocation.end_at).getTime() + shiftMs
    ).toISOString();
  }

  if (new Date(newStart) >= new Date(newEnd)) {
    isResizing.value = false;
    resizeHandle.value = null;
    resizeOffset.value = 0;
    return;
  }

  emit('update', {
    id: props.allocation.id,
    start_at: newStart,
    end_at: newEnd,
  });

  didDragOrResize.value = true;
  setTimeout(() => {
    didDragOrResize.value = false;
  }, 50);

  nextTick(() => {
    isResizing.value = false;
    resizeHandle.value = null;
    resizeOffset.value = 0;
  });
};

const handleClick = e => {
  if (isDragging.value || isResizing.value || didDragOrResize.value) return;

  emit('click', {
    allocation: props.allocation,
    clientX: e.clientX,
    clientY: e.clientY,
  });
};

const handleContextMenu = e => {
  e.preventDefault();
  e.stopPropagation();

  emit('contextMenu', {
    allocation: props.allocation,
    clientX: e.clientX,
    clientY: e.clientY,
  });
};
</script>

<template>
  <div
    ref="blockRef"
    class="vertical-block absolute overflow-hidden cursor-pointer select-none rounded-lg border border-solid border-n-slate-6 bg-white dark:bg-n-solid-2"
    :class="{
      'right-1': !isChild,
      'left-1': !isChild,
      'right-1 left-4': isChild,
      'is-dragging shadow-2xl z-30 ring-2 ring-n-brand/50': isDragging,
      'hover:shadow-sm': !isDragging,
    }"
    :style="blockStyle"
    @mousedown="handleMouseDown"
    @click="handleClick"
    @contextmenu="handleContextMenu"
    @mouseenter="startHoverCard"
    @mouseleave="hideHoverCardSoon"
  >
    <div
      v-if="!disableResize"
      class="resize-handle absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-n-slate-8/30 z-10"
      @mousedown.stop="handleResizeStart('top', $event)"
    />

    <span
      v-if="accentColor"
      class="pointer-events-none absolute left-1.5 top-1.5 bottom-1.5 w-1 rounded-full"
      :style="{ backgroundColor: accentColor }"
    />

    <div
      v-if="isInvalid"
      class="i-lucide-alert-triangle w-3 h-3 text-red-500 absolute top-1.5 right-1.5 z-10"
    />

    <div class="flex items-start gap-2 min-w-0 w-full h-full pl-4 pr-1.5 py-1">
      <div class="flex flex-col justify-center min-w-0 flex-1">
        <span class="text-sm font-semibold truncate text-n-slate-12">
          {{ displayTitle }}
        </span>
        <div
          class="flex items-center gap-1 text-xs text-n-slate-11 mt-0.5 min-w-0"
        >
          <span class="i-lucide-clock size-3 shrink-0" aria-hidden="true" />
          <span class="truncate">{{ timeRangeLabel }}</span>
        </div>
      </div>
      <div class="flex items-start gap-1.5 shrink-0">
        <span
          v-if="eventLabel"
          class="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-n-slate-3 outline outline-1 outline-n-slate-6 text-xs font-semibold text-n-slate-12 max-w-[9rem]"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :style="{ backgroundColor: eventLabel.color }"
          />
          <span class="truncate">{{ eventLabel.title }}</span>
        </span>
        <span
          v-else-if="eventLabelColor"
          class="size-2 rounded-full flex-shrink-0 mt-1.5"
          :style="{ backgroundColor: eventLabelColor }"
        />
        <span
          v-if="slotCount > 1"
          class="flex items-center justify-center size-6 rounded-full bg-n-slate-4 text-xs font-semibold text-n-slate-12 shrink-0"
          :aria-label="t('PLANNING.SCHEDULER.SLOT_COUNT', { count: slotCount })"
        >
          {{ slotCount }}
        </span>
      </div>
    </div>

    <div
      v-if="!disableResize"
      class="resize-handle absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-n-slate-8/30 z-10"
      @mousedown.stop="handleResizeStart('bottom', $event)"
    />
  </div>

  <Teleport to="body">
    <EventHoverCard
      v-if="showHoverCard"
      :allocation="allocation"
      :event="hoverEvent"
      :slots="hoverSlots"
      :resources="resources"
      :accent-color="accentColor"
      :anchor-rect="hoverAnchorRect"
      :hovered-resource-id="allocation.resource_id"
      :hovered-slot-ids="hoveredSlotIds"
      @mouseenter="keepHoverCard"
      @mouseleave="hideHoverCardSoon"
    />
  </Teleport>
</template>

<style scoped>
.vertical-block.is-dragging {
  cursor: grabbing !important;
}

.vertical-block.is-dragging * {
  cursor: grabbing !important;
}

.resize-handle {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.vertical-block:hover .resize-handle {
  opacity: 1;
}
</style>
